import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from "firebase/auth";
import { useMutation, useApolloClient, gql } from "@apollo/client";
import { usePostHog } from "@posthog/react";
import { auth, googleProvider } from "@/utils/firebase";
import { useTrackEvent } from "@/hooks/useTrackEvent";

const CREATE_OR_UPDATE_USER = gql`
  mutation CreateOrUpdateUser($input: CreateUserInput!) {
    createOrUpdateUser(input: $input) {
      id
      email
      displayName
      photoUrl
      onboardingCompleted
    }
  }
`;

const ACCEPT_TEAM_INVITES = gql`
  mutation AcceptTeamInvites {
    acceptTeamInvites
  }
`;

const GET_ME = gql`
  query GetMe {
    me {
      id
      email
      displayName
      photoUrl
      firstName
      lastName
      onboardingCompleted
      marketplaceRole
      platformRole
    }
  }
`;

interface User {
  id: string;
  email: string;
  displayName: string | null;
  photoUrl: string | null;
  firstName?: string | null;
  lastName?: string | null;
  onboardingCompleted: boolean;
  /** Null until the user picks a side. Drives every marketplace route guard. */
  marketplaceRole?: "BRAND" | "CREATOR" | null;
  /**
   * Operator privilege, and a different axis from `marketplaceRole`. Only ever
   * a hint for what to render — every admin-only field is guarded server-side
   * by `requireAdmin`, which reads the user's own row.
   */
  platformRole?: "USER" | "ADMIN";
}

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const [createOrUpdateUser] = useMutation(CREATE_OR_UPDATE_USER);
  const [acceptTeamInvites] = useMutation(ACCEPT_TEAM_INVITES);
  const apolloClient = useApolloClient();
  const trackEvent = useTrackEvent();
  const posthog = usePostHog();

  // Tie the browser's PostHog identity to our DB user id, so frontend
  // autocaptured events land on the SAME person as backend (posthog-node)
  // events, which also use user.id as distinctId. Also fire session_started
  // once per app load, into our own events table (source of truth).
  const sessionLoggedFor = useRef<string | null>(null);
  useEffect(() => {
    if (user?.id && sessionLoggedFor.current !== user.id) {
      sessionLoggedFor.current = user.id;
      posthog?.identify?.(user.id, { email: user.email });
      trackEvent("session_started", { userId: user.id });
    }
  }, [user?.id, user?.email, posthog, trackEvent]);

  // Pull the user into any team they were invited to since last login. Runs
  // once per user per session; failures are non-fatal (they can retry later).
  const invitesAcceptedFor = useRef<string | null>(null);
  useEffect(() => {
    if (user?.id && invitesAcceptedFor.current !== user.id) {
      invitesAcceptedFor.current = user.id;
      acceptTeamInvites()
        .then(({ data }) => {
          if (data?.acceptTeamInvites > 0) {
            apolloClient.refetchQueries({ include: ["GetMyTeams"] });
          }
        })
        .catch(() => {
          /* non-fatal — invites can be accepted on a later login */
        });
    }
  }, [user?.id, acceptTeamInvites, apolloClient]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        try {
          // Try fetching existing user first
          const { data } = await apolloClient.query({
            query: GET_ME,
            fetchPolicy: "network-only",
          });

          if (data?.me) {
            setUser(data.me);
          } else {
            // User exists in Firebase but not in our DB — upsert
            const { data: mutData } = await createOrUpdateUser({
              variables: {
                input: {
                  email: fbUser.email,
                  displayName: fbUser.displayName,
                  photoUrl: fbUser.photoURL,
                },
              },
            });
            setUser(mutData?.createOrUpdateUser || null);
          }

          await apolloClient.refetchQueries({
            include: ["GetProperties", "GetProperty", "GetMyFavorites"],
          });
        } catch (error) {
          console.error("Error syncing user:", error);
          // GET_ME may fail if user doesn't exist yet — try upsert as fallback
          try {
            const { data: mutData } = await createOrUpdateUser({
              variables: {
                input: {
                  email: fbUser.email,
                  displayName: fbUser.displayName,
                  photoUrl: fbUser.photoURL,
                },
              },
            });
            setUser(mutData?.createOrUpdateUser || null);
          } catch (upsertError) {
            console.error("Error upserting user:", upsertError);
            setUser(null);
          }
        }
      } else {
        setUser(null);
        await apolloClient.resetStore();
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [createOrUpdateUser, apolloClient]);

  const signInWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    if (result.user) {
      // Ensure user is created/updated in our DB immediately after popup
      try {
        const { data } = await createOrUpdateUser({
          variables: {
            input: {
              email: result.user.email,
              displayName: result.user.displayName,
              photoUrl: result.user.photoURL,
            },
          },
        });
        if (data?.createOrUpdateUser) {
          setUser(data.createOrUpdateUser);
        }
      } catch (error) {
        console.error("Error creating user after sign-in:", error);
      }
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      sessionLoggedFor.current = null;
      posthog?.reset?.(); // drop identity so the next user isn't merged in
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  const updateUser = (updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : null));
  };

  const getIdToken = async (): Promise<string | null> => {
    if (!firebaseUser) return null;
    try {
      return await firebaseUser.getIdToken();
    } catch (error) {
      console.error("Error getting ID token:", error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        user,
        loading,
        signInWithGoogle,
        signOut,
        getIdToken,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
